// Placeholder token parser for `${name}` and `${expr}` forms.
// Example: parseLoopPlaceholderAt("${bp.size}", 0).
import type { ParsedLoopPlaceholder } from './types'
import { isValidLoopPlaceholderKey } from './identifiers'
// Check placeholder entry points.
// Check braced placeholder bodies.
// Check placeholder closing braces.
// Check direct-key placeholder bodies.
// Return placeholder parse results.
// Continue expression-mode parsing.

// Parse one loop placeholder token from source at the provided index.
export function parseLoopPlaceholderAt(source: string, startIndex: number): ParsedLoopPlaceholder | null {
  // Reject positions that do not start with `$`.
  if (source[startIndex] !== '$') {
    // Return null for non-placeholder positions.
    return null
  }

  // Reject unsupported plain `$name` placeholders.
  if (source[startIndex + 1] !== '{') {
    // Return null for non-braced loop placeholders.
    return null
  }

  // Return the parsed braced loop placeholder.
  return parseBracedLoopPlaceholder(source, startIndex)
}

// Parse `${name}` and `${expr}` placeholders with nested-paren support.
function parseBracedLoopPlaceholder(source: string, startIndex: number): ParsedLoopPlaceholder | null {
  const closeIndex = findPlaceholderCloseIndex(source, startIndex)
  // Reject malformed placeholders without closing `}`.
  if (closeIndex === -1) {
    // Return null for unterminated braced placeholders.
    return null
  }

  const content = source.slice(startIndex + 2, closeIndex).trim()
  // Reject empty placeholders like `${}`.
  if (!content) {
    // Return null for empty braced placeholders.
    return null
  }

  const mode = shouldUseDirectKeyMode(content) ? 'key' : 'expression'
  // Return the parsed braced placeholder descriptor.
  return {
    key: content,
    mode,
    start: startIndex,
    end: closeIndex + 1,
    raw: source.slice(startIndex, closeIndex + 1),
  }
}

// Find the real closing `}` for one braced placeholder token.
function findPlaceholderCloseIndex(source: string, startIndex: number): number {
  let depth = 1
  let quote: "'" | '"' | null = null

  // Scan the braced placeholder body until the matching `}`.
  for (let index = startIndex + 2; index < source.length; index += 1) {
    const char = source[index]
    const previous = source[index - 1] ?? ''
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      }
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '{') {
      // Reject nested braces because loop placeholders only allow expression groups.
      return -1
    }
    if (char === '}') {
      depth -= 1
      if (depth === 0) {
        // Return the matching closing-brace index.
        return index
      }
    }
  }

  // Return -1 for unterminated braced placeholders.
  return -1
}

// Check whether the placeholder body is a direct key lookup.
function shouldUseDirectKeyMode(content: string): boolean {
  // Reject key-mode detection when whitespace exists in the body.
  for (let index = 0; index < content.length; index += 1) {
    const char = content[index]
    if (char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f') {
      // Return false for expression-like whitespace usage.
      return false
    }
    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '(' || char === ')' || char === "'" || char === '"') {
      // Return false for operator-bearing expression bodies.
      return false
    }
  }

  // Return true only for direct placeholder key syntax.
  return isValidLoopPlaceholderKey(content)
}
